import React from 'react'
import { LocationDescriptorObject } from 'history'
import { useLocation } from 'react-router'

import {
  ComponentInfoFragment,
  FrameGroupDetailsFragment,
  useGetComponentsQuery,
} from '@sketch/gql-types'
import { dataIdFromObject } from '@sketch/graphql-cache'
import {
  Dropdown,
  handleFetchMore,
  InfiniteList,
  SFSymbolLabel,
} from '@sketch/components'

import { useVersioning } from '../../../versioning'
import NavigationItem from './NavigationItem'

interface SymbolNavigationItemProps {
  shareIdentifier: string
  frameGroup: FrameGroupDetailsFragment
}

const ENTRIES_PATH = ['share', 'version', 'document', 'components', 'entries']

// A symbol navigates to its own frame. Variant sets are skipped: their
// frames are fetched separately (see `VariantSetItem`), so the dropdown has
// no canvas location for them.
const getComponentFrameUuid = (
  component: ComponentInfoFragment
): string | undefined => {
  if (component.__typename === 'Symbol') {
    return component.frame?.uuid ?? undefined
  }

  return undefined
}

const SymbolDropdown = (props: SymbolNavigationItemProps) => {
  const { shareIdentifier, frameGroup } = props

  const { versionShortId, getPathname } = useVersioning()
  const { state } = useLocation()

  const { data, loading, fetchMore } = useGetComponentsQuery({
    variables: {
      shareIdentifier,
      path: frameGroup.component?.path,
      versionShortId,
      type: 'SYMBOL',
    },
  })

  const symbols = (
    data?.share?.version?.document?.components.entries || []
  ).filter(symbol => getComponentFrameUuid(symbol) !== undefined)

  const after = data?.share?.version?.document?.components.meta.after

  const onLoadMore = handleFetchMore(fetchMore, ENTRIES_PATH, {
    dataIdFromObject,
    after,
  })

  const getFrameLink = (
    symbol: ComponentInfoFragment
  ): LocationDescriptorObject => {
    return {
      pathname: getPathname({
        routeKey: 'FRAME',
        routeParams: {
          shareID: shareIdentifier,
          frameUUID: getComponentFrameUuid(symbol)!,
        },
      }),
      state,
    }
  }

  if (loading) {
    return (
      <>
        <Dropdown.Header>Symbols</Dropdown.Header>
        <Dropdown.Skeleton />
      </>
    )
  }

  return (
    <InfiniteList onLoadMore={onLoadMore}>
      <Dropdown.Header>Symbols</Dropdown.Header>
      {symbols.map(symbol => (
        <Dropdown.Item.NavLink
          key={symbol.identifier}
          isActive={match =>
            !!match || symbol.identifier === frameGroup?.identifier
          }
          to={getFrameLink(symbol)}
        >
          <SFSymbolLabel text={symbol.name} />
        </Dropdown.Item.NavLink>
      ))}
    </InfiniteList>
  )
}

export const SymbolNavigationItem = (props: SymbolNavigationItemProps) => {
  const { shareIdentifier, frameGroup } = props

  const { data } = useGetComponentsQuery({
    variables: {
      shareIdentifier,
      path: frameGroup.component?.path,
      type: 'SYMBOL',
      limit: 2,
    },
  })

  if (!frameGroup || !frameGroup.component) {
    return null
  }

  const hasComponents =
    (data?.share?.version?.document?.components.meta.totalCount || 0) > 1

  return (
    <NavigationItem
      icon="symbol"
      label={<SFSymbolLabel text={frameGroup.component.name} />}
      {...(hasComponents
        ? {
            dropdownComponent: SymbolDropdown,
            dropdownComponentProps: props,
            dropdownProps: { placement: 'bottom-start' },
          }
        : {})}
    />
  )
}
